'use client';
import { BrandReportState2 } from '@mnd-frontend/graphql-schema';
import { Text } from '@mnd-frontend/ui/publish/components';
import { useToasts } from '@mnd-frontend/ui/publish/contexts';
import { media } from '@mnd-frontend/ui/publish/utils';
import { GREY_800 } from '@mnd-frontend/ui/shared/tokens';
import styled from 'styled-components';
import { useReportUpdateInsightMutation } from '../../../../generated/hooks';
import { useReportDynamicData, useReportEditable, useReportTracking } from '../../hooks';
import { AllowNewLine } from './AllowNewline';
import { Card } from './Card';
import { SkeletonInsight } from './Skeleton';
import { TextInput } from './TextInput';

const FlexCard = styled(Card)`
  color: ${GREY_800};
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  > * {
    width: 100%;
  }
  ${media.lessThan('tablet')} {
    flex-wrap: wrap;
  }
`;

const InsightTitleStyle = styled.span`
  font-weight: 600;
  display: block;
`;

export const Insights = ({
  insights,
}: {
  insights: {
    id: string;
    title: string | null;
    insight: string;
    insightType: string;
  }[];
}) => {
  const { showFailureToast } = useToasts();
  const { track } = useReportTracking();
  const { editable } = useReportEditable();
  const { state } = useReportDynamicData();

  const [updateInsight] = useReportUpdateInsightMutation({
    onCompleted: data => {
      const error = data.reportUpdateInsight?.error;
      const errorMessage = error?.message;
      if (errorMessage) {
        showFailureToast(errorMessage);
      }
    },
    onError: () => showFailureToast(),
  });

  // NOTE: Reports built with the new insights setup will always have all the insights available.
  // If a report was built with the old setup, but migrated to the new design, it won't have insights for several sections.
  // After the migration from the old to the new design, these sections shouldn't show any component for the missing insights.
  if (state === BrandReportState2.Done && insights.length === 0) {
    return null;
  }

  if (insights.length === 0) {
    return (
      <FlexCard>
        <SkeletonInsight />
        <SkeletonInsight />
        <SkeletonInsight />
      </FlexCard>
    );
  }

  return (
    <FlexCard>
      {insights.map(insight =>
        insight ? (
          <div key={insight.id}>
            {editable ? (
              <Text as="p" variant="bodySmall" color="GREY_800">
                <InsightTitleStyle>
                  <TextInput
                    initialValue={insight.title || ''}
                    testId={`insight-${insight.insightType}-title`}
                    placeholder=""
                    onChange={value => {
                      track({
                        event: 'Brand Reports: Update AI insight',
                        traits: { insight: insight.insightType },
                      });
                      updateInsight({
                        variables: {
                          reportInsightId: insight.id,
                          title: value,
                        },
                      });
                    }}
                  />
                </InsightTitleStyle>
                <TextInput
                  initialValue={insight.insight}
                  testId={`insight-${insight.insightType}-insight`}
                  placeholder=""
                  allowNewline
                  onChange={value => {
                    track({
                      event: 'Brand Reports: Update AI insight',
                      traits: { insight: insight.insightType },
                    });
                    updateInsight({
                      variables: {
                        reportInsightId: insight.id,
                        insight: value,
                      },
                    });
                  }}
                />
              </Text>
            ) : (
              <Text as="p" variant="bodySmall" color="GREY_800">
                <InsightTitleStyle>{insight.title}</InsightTitleStyle>
                <AllowNewLine>{insight.insight}</AllowNewLine>
              </Text>
            )}
          </div>
        ) : null,
      )}
    </FlexCard>
  );
};
